"use client";

import { c as _c } from "react/compiler-runtime";
import { MapPin, Stethoscope } from "lucide-react";
import { FIELD_CLASSNAME, type OnlineSchedulingLocation, type OnlineSchedulingProvider } from "@/src/components/online-scheduling/types";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/src/components/shadcn/ui/select";
import { ExtraSmall } from "@/src/components/typography";
import { cn } from "@/src/lib/shadcn-utils";
import type { ReactNode } from "react";
const ANY_PROVIDER_VALUE = "any";
export function ProviderSelectionField(t0) {
  const $ = _c(11);
  const {
    providers,
    selectedProviderId,
    onChange,
    allowAny: t1,
    readOnly: t2
  } = t0;
  const allowAny = t1 === undefined ? false : t1;
  const readOnly = t2 === undefined ? false : t2;
  let t3;
  if ($[0] !== providers || $[1] !== selectedProviderId) {
    t3 = selectedProviderId === null ? "Any available provider" : providers.find(provider => provider.practitionerId === selectedProviderId)?.displayName;
    $[0] = providers;
    $[1] = selectedProviderId;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const selectedLabel = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Stethoscope className="h-4 w-4" />;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== allowAny || $[5] !== onChange || $[6] !== providers || $[7] !== readOnly || $[8] !== selectedLabel || $[9] !== selectedProviderId) {
    t5 = <SelectionField icon={t4} label="Provider">{readOnly ? <p className="truncate text-sm font-medium text-foreground">{selectedLabel}</p> : <Select value={selectedProviderId === undefined ? undefined : selectedProviderId === null ? ANY_PROVIDER_VALUE : selectedProviderId} onValueChange={value => onChange(value === ANY_PROVIDER_VALUE ? null : value)}><SelectionTrigger placeholder="Choose a provider" /><SelectContent>{allowAny && <SelectItem value={ANY_PROVIDER_VALUE}>Any available provider</SelectItem>}{providers.map(_temp)}</SelectContent></Select>}</SelectionField>;
    $[4] = allowAny;
    $[5] = onChange;
    $[6] = providers;
    $[7] = readOnly;
    $[8] = selectedLabel;
    $[9] = selectedProviderId;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}
function _temp(provider_0) {
  return <SelectItem key={provider_0.practitionerId} value={provider_0.practitionerId}>{provider_0.displayName}</SelectItem>;
}
export function LocationSelectionField(t0) {
  const $ = _c(6);
  const {
    locations,
    selected,
    onChange,
    readOnly: t1
  } = t0;
  const readOnly = t1 === undefined ? false : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <MapPin className="h-4 w-4" />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== locations || $[2] !== onChange || $[3] !== readOnly || $[4] !== selected) {
    t3 = <SelectionField icon={t2} label="Location">{readOnly || locations.length === 1 && selected ? <p className="truncate text-sm font-medium text-foreground">{selected?.displayName ?? "Choose a location"}</p> : <Select value={selected?.locationId} onValueChange={value => {
        const next = locations.find(location => location.locationId === value);
        if (next) {
          onChange(next);
        }
      }}><SelectionTrigger placeholder="Choose a location" /><SelectContent>{locations.map(_temp2)}</SelectContent></Select>}</SelectionField>;
    $[1] = locations;
    $[2] = onChange;
    $[3] = readOnly;
    $[4] = selected;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
function _temp2(location_0) {
  return <SelectItem key={location_0.locationId} value={location_0.locationId}>{location_0.displayName}</SelectItem>;
}
function SelectionField(t0) {
  const $ = _c(11);
  const {
    icon,
    label,
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn("flex items-center gap-2 rounded-xl border px-4 py-4", FIELD_CLASSNAME);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== icon) {
    t2 = <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent text-foreground">{icon}</span>;
    $[1] = icon;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== label) {
    t3 = <ExtraSmall className="uppercase tracking-[0.12em] text-muted-foreground">{label}</ExtraSmall>;
    $[3] = label;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== children || $[6] !== t3) {
    t4 = <div className="flex min-w-0 flex-1 flex-col">{t3}{children}</div>;
    $[5] = children;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t2 || $[9] !== t4) {
    t5 = <div className={t1}>{t2}{t4}</div>;
    $[8] = t2;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}
function SelectionTrigger(t0) {
  const $ = _c(2);
  const {
    placeholder
  } = t0;
  let t1;
  if ($[0] !== placeholder) {
    t1 = <SelectTrigger className="h-auto border-0 bg-transparent px-0 py-0 text-sm font-medium shadow-none focus:ring-0 disabled:opacity-100 [&>span]:truncate"><SelectValue placeholder={placeholder} /></SelectTrigger>;
    $[0] = placeholder;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}